import { useState, useRef, useCallback } from 'react';
import { globalKnowledgeService, ATOUploadResult } from '@/services/api/global-knowledge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Upload,
  FileText,
  Play,
  Square,
  Trash2,
  CheckCircle,
  XCircle,
  Loader2,
  Clock,
  AlertTriangle,
  FolderOpen,
} from 'lucide-react';

interface UploadFileEntry {
  id: string;
  file: File;
  filename: string;
  status: 'pending' | 'uploading' | 'indexed' | 'failed' | 'duplicate';
  result?: ATOUploadResult;
  error?: string;
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <Clock className="h-4 w-4 text-muted-foreground" />,
  uploading: <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />,
  indexed: <CheckCircle className="h-4 w-4 text-green-500" />,
  failed: <XCircle className="h-4 w-4 text-red-500" />,
  duplicate: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  uploading: 'Processing...',
  indexed: 'Indexed',
  failed: 'Failed',
  duplicate: 'Duplicate',
};

export function ATOUploadPage() {
  const [files, setFiles] = useState<UploadFileEntry[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [shouldStop, setShouldStop] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stopRef = useRef(false);

  // Add files from input or drag
  const addFiles = useCallback((fileList: FileList | File[]) => {
    const newFiles: UploadFileEntry[] = [];
    const existingNames = new Set(files.map(f => f.filename));

    for (const file of Array.from(fileList)) {
      if (!file.name.endsWith('.txt')) continue;
      if (existingNames.has(file.name)) continue;
      newFiles.push({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        filename: file.name,
        status: 'pending',
      });
    }

    if (newFiles.length > 0) {
      setFiles(prev => [...prev, ...newFiles]);
    }
  }, [files]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const clearAll = () => {
    if (!isProcessing) {
      setFiles([]);
    }
  };

  const clearCompleted = () => {
    if (!isProcessing) {
      setFiles(prev => prev.filter(f => f.status === 'pending' || f.status === 'failed'));
    }
  };

  const removeFile = (id: string) => {
    if (!isProcessing) {
      setFiles(prev => prev.filter(f => f.id !== id));
    }
  };

  // Process queue sequentially
  const startProcessing = async () => {
    setIsProcessing(true);
    setShouldStop(false);
    stopRef.current = false;

    for (let i = 0; i < files.length; i++) {
      if (stopRef.current) break;

      const entry = files[i];
      if (entry.status !== 'pending') continue;

      // Update status to uploading
      setFiles(prev => prev.map(f =>
        f.id === entry.id ? { ...f, status: 'uploading' as const } : f
      ));

      try {
        const content = await readFileAsText(entry.file);
        const result = await globalKnowledgeService.uploadATODocument(entry.filename, content);

        setFiles(prev => prev.map(f =>
          f.id === entry.id ? { ...f, status: 'indexed' as const, result } : f
        ));
      } catch (err: any) {
        const isDuplicate = err?.response?.status === 409;
        const errorMsg = err?.response?.data?.error || err?.message || 'Unknown error';

        setFiles(prev => prev.map(f =>
          f.id === entry.id
            ? { ...f, status: isDuplicate ? 'duplicate' as const : 'failed' as const, error: errorMsg }
            : f
        ));
      }
    }

    setIsProcessing(false);
    setShouldStop(false);
  };

  const stopProcessing = () => {
    stopRef.current = true;
    setShouldStop(true);
  };

  // Stats
  const counts = {
    total: files.length,
    pending: files.filter(f => f.status === 'pending').length,
    uploading: files.filter(f => f.status === 'uploading').length,
    indexed: files.filter(f => f.status === 'indexed').length,
    failed: files.filter(f => f.status === 'failed').length,
    duplicate: files.filter(f => f.status === 'duplicate').length,
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 px-6 py-4 border-b bg-background">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Upload className="h-5 w-5" />
          ATO Document Upload
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Upload ATO crawler .txt files for batch indexing and vectorization
        </p>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
        {/* Drop zone */}
        <div
          className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            hover:border-primary/50 hover:bg-muted/30 transition-colors"
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <FolderOpen className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-sm font-medium">
            Drop .txt files here or click to browse
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Accepts ATO crawler output files (.txt format)
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>

        {/* Actions + Stats */}
        {files.length > 0 && (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {!isProcessing ? (
                <Button
                  onClick={startProcessing}
                  disabled={counts.pending === 0}
                  size="sm"
                >
                  <Play className="h-3.5 w-3.5 mr-1.5" />
                  Start Processing ({counts.pending})
                </Button>
              ) : (
                <Button
                  onClick={stopProcessing}
                  variant="destructive"
                  size="sm"
                  disabled={shouldStop}
                >
                  <Square className="h-3.5 w-3.5 mr-1.5" />
                  {shouldStop ? 'Stopping...' : 'Stop'}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={clearCompleted}
                disabled={isProcessing || counts.indexed + counts.duplicate === 0}
              >
                Clear Completed
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                disabled={isProcessing}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Clear All
              </Button>
            </div>

            <div className="flex items-center gap-3 text-xs">
              <span className="text-muted-foreground">{counts.total} files</span>
              {counts.indexed > 0 && (
                <Badge variant="default" className="text-[10px] gap-1">
                  <CheckCircle className="h-2.5 w-2.5" /> {counts.indexed}
                </Badge>
              )}
              {counts.uploading > 0 && (
                <Badge variant="secondary" className="text-[10px] gap-1">
                  <Loader2 className="h-2.5 w-2.5 animate-spin" /> {counts.uploading}
                </Badge>
              )}
              {counts.failed > 0 && (
                <Badge variant="destructive" className="text-[10px] gap-1">
                  <XCircle className="h-2.5 w-2.5" /> {counts.failed}
                </Badge>
              )}
              {counts.duplicate > 0 && (
                <Badge variant="outline" className="text-[10px] gap-1 text-yellow-600">
                  <AlertTriangle className="h-2.5 w-2.5" /> {counts.duplicate}
                </Badge>
              )}
              {counts.pending > 0 && (
                <Badge variant="outline" className="text-[10px] gap-1">
                  <Clock className="h-2.5 w-2.5" /> {counts.pending}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* File Queue */}
        {files.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <div className="max-h-[calc(100vh-380px)] overflow-y-auto">
                <div className="divide-y">
                  {files.map((entry) => (
                    <div
                      key={entry.id}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm
                        ${entry.status === 'uploading' ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''}
                        ${entry.status === 'indexed' ? 'bg-green-50/30 dark:bg-green-950/10' : ''}
                        ${entry.status === 'failed' ? 'bg-red-50/30 dark:bg-red-950/10' : ''}
                        ${entry.status === 'duplicate' ? 'bg-yellow-50/30 dark:bg-yellow-950/10' : ''}
                      `}
                    >
                      {/* Status icon */}
                      <div className="shrink-0">{STATUS_ICONS[entry.status]}</div>

                      {/* Filename */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="truncate font-medium">{entry.filename}</span>
                        </div>
                        {entry.error && (
                          <p className="text-xs text-red-500 mt-0.5 truncate">{entry.error}</p>
                        )}
                        {entry.result?.sourceUrl && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {entry.result.sourceUrl.replace('https://www.ato.gov.au', '')}
                          </p>
                        )}
                      </div>

                      {/* Category badge */}
                      {entry.result?.categoryName && (
                        <Badge variant="outline" className="text-[10px] shrink-0">
                          {entry.result.categoryName}
                        </Badge>
                      )}

                      {/* Chunks */}
                      {entry.result?.chunkCount && (
                        <span className="text-xs text-muted-foreground shrink-0">
                          {entry.result.chunkCount} chunks
                        </span>
                      )}

                      {/* Status label */}
                      <span className={`text-xs shrink-0 min-w-[70px] text-right
                        ${entry.status === 'indexed' ? 'text-green-600' : ''}
                        ${entry.status === 'failed' ? 'text-red-600' : ''}
                        ${entry.status === 'duplicate' ? 'text-yellow-600' : ''}
                        ${entry.status === 'uploading' ? 'text-blue-600' : ''}
                        ${entry.status === 'pending' ? 'text-muted-foreground' : ''}
                      `}>
                        {STATUS_LABELS[entry.status]}
                      </span>

                      {/* Remove button */}
                      {!isProcessing && entry.status !== 'uploading' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 shrink-0"
                          onClick={() => removeFile(entry.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {files.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="h-16 w-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">No files selected</p>
            <p className="text-sm mt-1">
              Select ATO crawler .txt files to begin batch upload and indexing
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
